import entityFieldMetaStore from "data/meta/entityFieldMetaStore";
import Q from "lib/Q";

export const getCandidateFilter = (fieldInfo, instance) => {
    const func = fieldInfo.scripts.filter;
    if (func == null) return () => true;
    return it => func(instance, it);
};

const ERR_UNSUPPORTED_REF = "Unsupported interpolation ref";
const ERR_UNSUPPORTED_TYPE = "Unsupported interpolation type";
const ERR_UNSUPPORTED_VALUE = "Unsupported interpolation value";

export function asLiteral(type, value) {
    switch (type) {
        // a mixture of AS VariableType names and HO XxxField class names
        case "Boolean":
            return value == null
                ? "_null(true)"
                : value.toString();
        case "DateField":
            return value == null
                ? `_null(createDate(1970,1,1))`
                : Q.value(value, "DATE").toAS();
        case "Date":
        case "DateTimeField":
            return value == null
                ? `_null(createDateTime(1970,1,1,0,0,0))`
                : Q.value(value, "DATETIME").toAS();
        case "Number":
        case "DecimalField":
            return value == null || isNaN(value)
                ? `_null(0.0)`
                : value.toString();
        case "int":
        case "IntegerField":
            return value == null || isNaN(value)
                ? `_null(0)`
                : Math.round(value).toString();
        case "ChoiceField":
        case "String":
        case "StringField":
            if (value == null) return `_null("")`;
            if (value.indexOf("\"") >= 0) {
                const e = new Error(ERR_UNSUPPORTED_VALUE);
                e.type = type;
                e.value = value;
                throw e;
            }
            return `"${value}"`;
        case "Time":
        case "TimeField":
            return value == null
                ? `_null(createTime(0,0,0))`
                : Q.value(value, "TIME").toAS();
        default:
            const e = new Error(ERR_UNSUPPORTED_TYPE);
            e.type = type;
            throw e;
    }
}

export const buildInterpolator = interp => {
    interp.refs.forEach(r => {
        if (r.expr) {
            // eslint-disable-next-line no-new-func
            r.expr = Function("instance", `return ${r.expr};`);
        } else if (r.name) {
            // old untyped refchain style
            r.expr = instance => {
                const path = r.name.split(".");
                path.shift(); // "instance"
                if (!r.type) {
                    r.type = path.reduce((ek, segment) => {
                        const fi = entityFieldMetaStore.getInfo(ek, segment);
                        return fi.otherEntityKey || fi.type;
                    }, instance.entityKey);
                }
                return ArmusScript._traverse(instance, path);
            };
        } else {
            throw new Error(ERR_UNSUPPORTED_REF);
        }
    });
    return instance => {
        const binding = new Map();
        interp.refs.forEach(r =>
            binding.set(r.id, asLiteral(r.type, r.expr(instance))));
        const shards = [];
        interp.parts.forEach((p, i) => {
            if (i > 0) {
                shards.push(binding.get(interp.ids[i - 1]));
            }
            shards.push(p);
        });
        return shards.join("");
    };
};

export const getPartialCandidateFilter = (fieldInfo, instance) => {
    // something returned from buildInterpolator (above)
    const func = fieldInfo.scripts.partialFilter;
    if (func == null) return null;
    try {
        return func(instance);
    } catch (e) {
        if (e.message === ERR_UNSUPPORTED_REF) {
            console.warn(`Field ${instance.entityKey}.${fieldInfo.key}'s candidate filter has a malformed reference, so cannot be partially applied for server-side processing.`);
            return null;
        }
        if (e.message === ERR_UNSUPPORTED_TYPE) {
            console.warn(`Field ${instance.entityKey}.${fieldInfo.key}'s candidate filter uses a ${e.type} reference, so cannot be partially applied for server-side processing.`);
            return null;
        }
        if (e.message === ERR_UNSUPPORTED_VALUE) {
            console.warn(`Field ${instance.entityKey}.${fieldInfo.key}'s candidate filter uses an unsupported ${e.type} value, so cannot be partially applied for server-side processing.`, e.value);
            return null;
        }
        throw e;
    }
};

export const isVersionInfluencing = (entityKeyOrInfo, fieldKeyOrInfo) => {
    if (typeof fieldKeyOrInfo === "string") {
        const ek = typeof entityKeyOrInfo === "string"
            ? entityKeyOrInfo
            : entityKeyOrInfo.key;
        fieldKeyOrInfo = entityFieldMetaStore.getInfo(ek, fieldKeyOrInfo);
    }
    return fieldKeyOrInfo.versionInfluencing;
};



// WEBPACK FOOTER //
// ./app/data/selectors/entityFields.js